import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import StockoutSummaryComponent from "./stockout-summary.component";
import { STOCKOUT_SUMMARY_COLUMNS } from "./card/stockout-summary.columns";
import stockoutApi from "../../../../configs/apis/stockout-summary";
import { downloadExportedScanTrackSummary } from "../../utils/export-utils";
import { getCompanyList } from "../../../../configs/apis/serialnumber-summary.js/get-companyId.api";
import {
  fetchStockoutSummaryStart,
  fetchStockoutSummarySuccess,
  fetchStockoutSummaryFailure,
  setFilters,
  setVisibleColumns,
  setItemPerPage,
  setPage,
} from "../../redux/stockout-number-summary/stockout-number-summary.slice";
import { getDateRange, getMonthOptions } from "../../utils/date-utils";

export default function StockoutSummaryContainer() {
  const [company, setCompany] = useState("");
  const [companyOptions, setCompanyOptions] = useState([]);
  // Initialize startDate/endDate to current month range using date-utils
  const currentYear = new Date().getFullYear().toString();
  const currentMonth = getMonthOptions()[new Date().getMonth()];
  const { startDate: initialStartDate, endDate: initialEndDate } = getDateRange(
    {
      year: currentYear,
      month: currentMonth,
    }
  );

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [isExporting, setIsExporting] = useState(false);
  const [exportingType, setExportingType] = useState("");
  const [exportError, setExportError] = useState(null);

  const {
    data,
    isLoading,
    filters,
    visibleColumns,
    itemPerPage,
    page,
    total,
    start,
    length,
    filteredCount,
    draw,
  } = useSelector((state) => state.stockoutSummary);

  const dispatch = useDispatch();

  const columnView = Object.fromEntries(visibleColumns);

  const handleColumnChange = (column) => {
    const updated = visibleColumns.map(([key, value]) =>
      key === column ? [key, !value] : [key, value]
    );
    dispatch(setVisibleColumns(updated));
  };

  const handleExport = async (format) => {
    setIsExporting(true);
    setExportingType(format);
    setExportError(null);
    try {
      const companyName = companyOptions.find(
        (opt) => opt.value === company
      )?.label;

      // Get only visible columns for export
      const visibleColumnKeys = visibleColumns
        .filter(([key, isVisible]) => isVisible)
        .map(([key]) => key);

      const params = {
        company_id: company,
        start_date: startDate,
        end_date: endDate,
        columns: visibleColumnKeys,
        file_name: `stockout_report_${companyName || "export"}_${new Date()
          .toISOString()
          .slice(0, 7)
          .replace("-", "")}`,
        fileType: format === "CSV" ? "csv" : "xlsx",
      };

      const response = await stockoutApi.exportStockOutSummary(params);
      downloadExportedScanTrackSummary(response, format, companyName);
    } catch (error) {
      setExportError(error.message || "Export failed");
    } finally {
      setIsExporting(false);
      setExportingType("");
    }
  };

  const handlePageClick = (newPage) => {
    dispatch(setPage(newPage));
  };

  const handleItemPerPageChange = (newItemPerPage) => {
    dispatch(setItemPerPage(newItemPerPage));
    dispatch(setPage(0)); // Reset to first page
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const result = await getCompanyList();
        if (result.success && Array.isArray(result.data)) {
          const options = result.data.map((c) => ({
            value: c.id,
            label: c.name,
          }));
          setCompanyOptions(options);
          // Auto-select company if only one is available
          if (options.length === 1) {
            setCompany(options[0].value);
          }
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (!company) return;
    dispatch(fetchStockoutSummaryStart());
    const fetchData = async () => {
      try {
        const startParam = page * itemPerPage;
        const lengthParam = itemPerPage;
        const result = await stockoutApi.getStockOutSummary({
          company_id: company,
          start_date: startDate,
          end_date: endDate,
          start: startParam,
          length: lengthParam,
        });

        // Use API response directly - no mapping needed
        let dataArray = [];
        if (result.data && Array.isArray(result.data.data)) {
          dataArray = result.data.data;
        } else if (Array.isArray(result.data)) {
          dataArray = result.data;
        } else if (Array.isArray(result)) {
          dataArray = result;
        }

        const mappedData = dataArray; // No transformation needed
        dispatch(
          fetchStockoutSummarySuccess({
            data: mappedData,
            total: result.data?.total_records,
            start: result.data?.start ?? startParam,
            length: result.data?.length ?? lengthParam,
            filteredCount: result.data?.total_records ?? mappedData.length,
            draw: result.data?.draw ?? 1,
          })
        );
      } catch (error) {
        console.error("Stockout Summary API error:", error);
        dispatch(
          fetchStockoutSummaryFailure(error.message || "Failed to fetch data")
        );
      }
    };
    fetchData();
  }, [company, startDate, endDate, page, itemPerPage, dispatch]);

  const getColumnLabel = (key) =>
    STOCKOUT_SUMMARY_COLUMNS.find((col) => col.key === key)?.label || key;

  const checkedColumns = visibleColumns.filter(
    ([key, value]) => value && key && key.trim() !== ""
  );

  return (
    <StockoutSummaryComponent
      company={company}
      setCompany={setCompany}
      startDate={startDate}
      setStartDate={setStartDate}
      endDate={endDate}
      setEndDate={setEndDate}
      companyOptions={companyOptions}
      columnView={columnView}
      handleColumnChange={handleColumnChange}
      handleExport={handleExport}
      visibleColumns={checkedColumns}
      getColumnLabel={getColumnLabel}
      STOCKOUT_SUMMARY_COLUMNS={STOCKOUT_SUMMARY_COLUMNS}
      data={data}
      isLoading={isLoading}
      itemPerPage={itemPerPage}
      page={page}
      total={total}
      onPageClick={handlePageClick}
      onItemPerPageChange={handleItemPerPageChange}
      filteredCount={filteredCount}
      start={start}
      length={length}
      draw={draw}
      isExporting={isExporting}
      exportingType={exportingType}
      exportError={exportError}
    />
  );
}
